// Browse tours — the COMPLETE list/grid view (built by the foundation agent).
//
// Each card is one derived tour (a tourTag slug): friendly title, item count, and
// a cover thumbnail (the first ordered item's thumbnail, falling back to its
// canonical/original photo). Tapping a card opens the detail view. A subtle line
// surfaces how many artifacts carry no tour tag at all.

import { imageUrl } from "../config";
import type { Artifact } from "../types";
import { ALL_TOURS_SLUG, type ExportState } from "../useTours";
import {
  artifactLabel,
  firstPhoto,
  slideCounts,
  tourTitle,
  untaggedCount,
  type TourGroup,
} from "../tours";
import { ArtImage } from "../components/ArtImage";
import "./TourListView.css";

interface TourListViewProps {
  groups: TourGroup[];
  /** Every artifact, used only for the subtle "untagged" count. */
  allArtifacts: Artifact[];
  loading: boolean;
  onOpenTour: (slug: string) => void;
  /** (Re)build a tour's offline HTML on the server, then download it. */
  exportTour: (slug: string) => Promise<void>;
  /** Current export status/message/url for a slug. */
  exportState: (slug: string) => ExportState;
}

export function TourListView({
  groups,
  allArtifacts,
  loading,
  onOpenTour,
  exportTour,
  exportState,
}: TourListViewProps) {
  if (loading && groups.length === 0) {
    return <div className="empty-state">Loading tours…</div>;
  }

  if (groups.length === 0) {
    return (
      <div className="empty-state">
        <p>No tours yet.</p>
        <p>
          A tour is just a tag on your artworks. Tag some artworks with the same
          tour name in the ingest app, and they’ll show up here.
        </p>
      </div>
    );
  }

  const untagged = untaggedCount(allArtifacts);

  // "Download all tours" = the multi-tour catalog (one file, front-page grid of
  // tour thumbnails + every tour). Reuses the per-slug export lifecycle keyed by
  // the ALL_TOURS_SLUG sentinel.
  const allXp = exportState(ALL_TOURS_SLUG);
  const allExporting = allXp.status === "generating";

  return (
    <div className="tour-list">
      <div className="tour-list__toolbar">
        <button
          type="button"
          className="tour-list__download-all"
          onClick={() => {
            if (!allExporting) void exportTour(ALL_TOURS_SLUG);
          }}
          disabled={allExporting}
          aria-busy={allExporting}
          title="Build ONE offline file with every tour (a front page of thumbnails, then each tour)"
        >
          {allExporting ? (
            <>
              <span className="tour-card__spinner" aria-hidden="true" />
              Building one file…
            </>
          ) : (
            "⬇ Download all tours (one file)"
          )}
        </button>
        <p
          className={`tour-list__download-all-note${
            allXp.status === "error" ? " tour-list__download-all-note--error" : ""
          }`}
          aria-live="polite"
        >
          {allXp.status === "error"
            ? `Couldn't build the file: ${allXp.message ?? "please try again."}`
            : allXp.status === "done"
              ? `✓ Downloaded. ${allXp.message ?? ""} Re-download after you change tours to refresh it.`
              : "One file with every tour — a front page of thumbnails, then each tour. Works fully offline (no Wi-Fi needed)."}
        </p>
      </div>

      <ul className="tour-list__grid">
        {groups.map((g) => {
          const cover = firstPhoto(g.slides);
          const coverSrc = cover
            ? imageUrl(cover.thumbnailKey) ?? imageUrl(cover.canonicalImageKey)
            : undefined;
          const coverFallback = cover ? imageUrl(cover.originalImageKey) : undefined;
          const { photos, quotes } = slideCounts(g.slides);
          const xp = exportState(g.slug);
          const exporting = xp.status === "generating";
          return (
            <li key={g.slug} className="tour-list__item">
              <button
                type="button"
                className="tour-card"
                onClick={() => onOpenTour(g.slug)}
              >
                <div className="tour-card__cover">
                  {cover ? (
                    <ArtImage
                      src={coverSrc}
                      fallbackSrc={coverFallback}
                      alt={artifactLabel(cover)}
                      className="tour-card__img"
                    />
                  ) : (
                    <div className="tour-card__cover-quote" aria-hidden="true">
                      <span className="tour-card__quote-mark">&ldquo;</span>
                    </div>
                  )}
                </div>
                <div className="tour-card__meta">
                  <span className="tour-card__title">{tourTitle(g.slug)}</span>
                  <span className="tour-card__count">
                    {photos} {photos === 1 ? "artwork" : "artworks"}
                    {quotes > 0 && (
                      <> · {quotes} quote{quotes === 1 ? "" : "s"}</>
                    )}
                  </span>
                </div>
              </button>
              {/* Sibling of the card (not nested — a button can't contain a
                  button): one-tap offline download for this tour. */}
              <button
                type="button"
                className="tour-card__download"
                onClick={() => {
                  if (!exporting) void exportTour(g.slug);
                }}
                disabled={exporting}
                aria-busy={exporting}
                title={
                  xp.status === "error"
                    ? `Couldn't build the file: ${xp.message ?? "tap to retry"}`
                    : "Download for iPad (offline file)"
                }
                aria-label={`Download "${tourTitle(g.slug)}" for iPad`}
              >
                {exporting ? (
                  <span className="tour-card__spinner" aria-hidden="true" />
                ) : (
                  "⬇"
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {untagged > 0 && (
        <p className="tour-list__untagged">
          {untagged} artwork{untagged === 1 ? "" : "s"} not in any tour.
        </p>
      )}
    </div>
  );
}
